import * as React from "react";
import { MapPin, ChevronRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
const topLocations = ["תל אביב", "ירושלים", "חיפה", "ראשון לציון", "פתח תקווה", "אשדוד", "נתניה", "בני ברק", "באר שבע", "חולון", "רמת גן", "רחובות"];
const otherLocations = ["אשקלון", "בת ים", "בית שמש", "כפר סבא", "הרצליה", "חדרה", "מודיעין", "רמלה", "רעננה", "מודיעין עילית", "הוד השרון", "גבעתיים", "קרית אתא", "נהריה", "ביתר עילית", "אום אל-פחם", "קרית גת", "אילת", "עכו", "עפולה", "כרמיאל", "טבריה", "אלעד", "ראש העין", "קרית מוצקין", "נתיבות", "קרית ים", "קרית ביאליק", "נס ציונה", "אור יהודה", "יבנה", "צפת", "קרית שמונה", "דימונה", "אופקים", "שדרות"];
const getCityUrl = (city: string) => {
  const fallbackPath = "/CaregiverServicePagesPage";
  const specificPages: Record<string, string> = {
    "תל אביב": "/NursingCaregiverInTelAvivWithinFiveHours",
    "חיפה": "/NursingCareProviderInHaifaWithinHours",
    "רמת גן": "/NursingCareInRamatGanWithinHours",
    "ירושלים": "/NursingCareProviderInJerusalemWithinFiveHours",
    "פתח תקווה": "/NursingCareProviderPetahTikvaWithinHours",
    "ראשון לציון": "/NursingCaregiverInRishonLeZionWithinFiveHours",
    "נתניה": "/NursingCareProviderNetanyaWithinFiveHours",
    "אשדוד": "/NursingCaregiverAshdodWithinHours",
    "באר שבע": "/NursingCareProviderInBeershevaWithinHours",
    "חולון": "/NursingCaregiverInHolonWithinHours",
    "רחובות": "/NursingCareProviderRehovot",
    "הרצליה": "/NursingCareProviderInHerzliyaWithinFiveHours",
    "כפר סבא": "/NursingCareProviderInKfarSabaWithinFiveHours",
    "מודיעין": "/NursingCaregiverInModiinWithinFiveHours",
    "אשקלון": "/NursingCareProviderInAshkelonWithinFiveHours",
    "חדרה": fallbackPath,
    "רעננה": fallbackPath
  };
  if (specificPages[city]) return specificPages[city];
  const citySlugs: Record<string, string> = {
    "ירושלים": "jerusalem",
    "ראשון לציון": "rishon-lezion",
    "פתח תקווה": "petah-tikva",
    "אשדוד": "ashdod",
    "נתניה": "netanya",
    "בני ברק": "bnei-brak",
    "באר שבע": "beer-sheva",
    "חולון": "holon",
    "רחובות": "rehovot",
    "אשקלון": "ashkelon",
    "בת ים": "bat-yam",
    "בית שמש": "beit-shemesh",
    "כפר סבא": "kfar-saba",
    "הרצליה": "herzliya",
    "חדרה": "hadera",
    "מודיעין": "modiin",
    "רמלה": "ramla",
    "רעננה": "raanana",
    "מודיעין עילית": "modiin-illit",
    "הוד השרון": "hod-hasharon",
    "גבעתיים": "givatayim",
    "קרית אתא": "kiryat-ata",
    "נהריה": "nahariya",
    "ביתר עילית": "beitar-illit",
    "אום אל-פחם": "umm-al-fahm",
    "קרית גת": "kiryat-gat",
    "אילת": "eilat",
    "עכו": "acco",
    "עפולה": "afulla",
    "כרמיאל": "karmiel",
    "טבריה": "tiberias",
    "אלעד": "elad",
    "ראש העין": "rosh-haayin",
    "קרית מוצקין": "kiryat-motzkin",
    "נתיבות": "netivot",
    "קרית ים": "kiryat-yam",
    "קרית ביאליק": "kiryat-bialik",
    "נס ציונה": "ness-ziona",
    "אור יהודה": "or-yehuda",
    "יבנה": "yavne",
    "צפת": "safed",
    "קרית שמונה": "kiryat-shmona",
    "דימונה": "dimona",
    "אופקים": "ofakim",
    "שדרות": "sderot"
  };
  const slug = citySlugs[city] || encodeURIComponent(city.replace(/\s+/g, "-"));
  return `/nursing/${slug}`;
};
export default function NursingLocationsGrid() {
  return <section id="סיעוד-בקרבת-מגוריכם" className="py-16 md:py-24 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
        <div className="text-center mb-10 md:mb-16">
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground mb-4">
            מטפל סיעודי בקרבת מגוריכם
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto font-default leading-relaxed">
            אנו ב-Siudy מספקים שירותי סיעוד רפואי בכל עיר בישראל, כולל זמינות בבתי החולים המובילים. בין אם אתם זקוקים ל
            {}
            <Link to={getCityUrl('תל אביב')} className="text-primary hover:underline font-medium mx-1">מטפל סיעודי בתל אביב</Link>, 
            שירותי 
            {}
            <Link to={getCityUrl('נתניה')} className="text-primary hover:underline font-medium mx-1">מטפל סיעודי בבית</Link> 
            או 
            {}
            <Link to={getCityUrl('ירושלים')} className="text-primary hover:underline font-medium mx-1">השגחה בבית חולים</Link>, 
            הצוות שלנו זמין עבורכם למתן מענה מקצועי תוך עד 5 שעות.
          </p>
          <div className="mt-4">
            {}
            <Link to={getCityUrl('תל אביב')} className="text-primary font-bold hover:underline transition-all">
              מטפל סיעודי
            </Link>
          </div>
        </div>

        <div className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-sm mb-6">
          <h3 className="text-xl font-semibold font-default text-foreground mb-6 flex items-center gap-2">
            <MapPin className="size-5 text-primary" />
            אזורי שירות מרכזיים
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {topLocations.map((city, i) => <Link key={i} data-index={i} to={getCityUrl(city)} className="block w-full h-full flex items-center gap-2 text-foreground hover:text-primary transition-colors group py-2">
                <ChevronRight className="size-4 text-muted-foreground/50 group-hover:text-primary transition-colors rtl:rotate-180" />
                <span className="font-default font-medium text-base">
                  מטפל סיעודי רפואי ב{city} בזמינות מיידית
                </span>
              </Link>)}
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full bg-card border border-border rounded-2xl px-6 md:px-8 shadow-sm">
          <AccordionItem value="all-cities" className="border-none">
            <AccordionTrigger className="text-lg font-semibold font-default text-foreground hover:no-underline hover:text-primary py-6">
              צפייה בכל אזורי השירות (50+ ערים)
            </AccordionTrigger>
            <AccordionContent>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-3 pb-6 pt-4 border-t border-border/50">
                {otherLocations.map((city, i) => <Link key={i} data-index={i} to={getCityUrl(city)} className="block w-full h-full flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors group py-1">
                    <span className="w-1.5 h-1.5 rounded-lg bg-muted-foreground/30 group-hover:bg-primary transition-colors shrink-0" />
                    <span className="font-default text-sm">
                      מטפל סיעודי רפואי ב{city} בזמינות מיידית
                    </span>
                  </Link>)}
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </section>;
}